import { FC, Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { showError } from 'utils/utils';
import { validate } from 'utils/validation';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import FormGroupSelect from 'components/ui/FormGroupSelect/FormGroupSelect';
import BookPayment from 'components/shared/BookPayment';
import { Credit2000PaymentInfo } from 'ts/shared/interfaces/Generic';
import GeneralConditionsModal from 'components/shared/GeneralConditionsModal';
import OrderTripCompany from 'components/shared/OrderTripCompany';
import PhoneInput from 'components/shared/ui/PhoneInput';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowCircleLeft, faUser } from '@fortawesome/free-solid-svg-icons';
import { sendOfflineRequest } from 'services/package';
import { DealModel, Pax } from 'ts/package/interfaces/PackageOfflineResponse';
import {
  PassengerType,
  getGender,
  getPackagePassengerValidation,
} from 'constants/package';
import { formatOccupancySrtByPaxes } from 'utils/package';

interface OrderBookProps {
  dealModel: DealModel;
  onClickBook: () => Promise<void>;
  couponAmount: string;
}

const OrderBook: FC<OrderBookProps> = ({ dealModel, ...props }) => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [paxesState, setPaxesState] = useState<Pax[]>(dealModel.paxes);
  const [isConditionsOpen, setIsConditionsOpen] = useState(false);
  const [companyId, setCompanyId] = useState('');
  const [tripId, setTripId] = useState('0');

  const onClickSend = async () => {
    setIsSubmitted(true);

    if (isValid()) {
      setIsLoading(true);
      const body = {
        ...dealModel!,
        paxes: paxesState,
        tripId,
        companyId,
        couponAmount: parseInt(props.couponAmount),
      };

      await props.onClickBook();

      const res = await sendOfflineRequest(body);
      if (res) {
        if (res.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (res.hasErrors) {
          console.log('OfflinePackages/BookComplete ' + res.errorMessage);
          showError(t('something_went_wrong'));
        } else {
          sessionStorage.removeItem('screenshotsOfflinePackages');
          sessionStorage.setItem('offPackageSuccessOrder', JSON.stringify(res.data));
          navigate(`/offline-package/success`);
        }
      }

      setIsLoading(false);
    }
  };
  const onClickSubmit = async (credit2000: Credit2000PaymentInfo) => {
    setIsSubmitted(true);

    if (isValid()) {
      setIsLoading(true);
      const body = {
        ...dealModel,
        paxes: paxesState.map((pax) => {
          return {
            ...pax,
            citizenship: 'IL',
          };
        }),
        creditCard: true,
        credit2000PaymentInfo: credit2000,
        paymentSettings: credit2000,
        tripId,
        companyId,
        couponAmount: parseInt(props.couponAmount),
      };

      await props.onClickBook();

      const response = await sendOfflineRequest(body);
      if (response) {
        if (response.sessionExpired) {
          showError(t('home.session_expired'));
        } else if (response.hasErrors) {
          console.log('OfflinePackages/BookComplete ' + response.errorMessage);
          showError(t('something_went_wrong'));
        } else {
          sessionStorage.removeItem('screenshotsOfflinePackages');
          sessionStorage.setItem('offPacPaymentOrder', JSON.stringify(response));
          navigate(`/offline-package/payment`);
        }
      }

      setIsLoading(false);
    }
  };

  const isValid = () => {
    let result = true;
    paxesState.forEach((pax, index) => {
      const validation = getPackagePassengerValidation(index);
      if (!validate(validation, pax)) {
        result = false;
      }
    });
    return result && termsAccepted;
  };

  const onChangeField = (pax: Pax, index: number) => {
    const newPaxesState = [...paxesState];
    newPaxesState[index] = pax;
    setPaxesState(newPaxesState);
  };

  return (
    <div className="details book">
      <div className="mb-20 default-shadow flex-between dyn-change-header">
        <Button
          title={t('back')}
          icon={faArrowCircleLeft}
          onClick={() => navigate(-1)}
          className="btn btn-link"
        />
      </div>
      <div className="book-form default-shadow">
        <div className="title-underline text-lg mb-5">{t('passenger.details')}</div>
        <div className="color-danger mb-10 text-xsm">{t('only_english_input')}</div>
        <div className="mb-20">
          <div className="text-cap text-contrast text-bold mb-10 flex-v-center flex-wrap">
            <FontAwesomeIcon icon={faUser} className="mr-10" />
            <span className="mr-10">{dealModel.room?.name?.name}</span>
            <span className="mr-10">{dealModel.room?.board?.name}</span>
            <span className="mr-10 ml-28">{formatOccupancySrtByPaxes(paxesState)}</span>
          </div>
          <div className="ph-10">
            {paxesState.map((pax, index) => (
              <div className="book-form-room" key={index}>
                <div className="title text-st mb-15">
                  <span className="mr-5">
                    {t('passenger.guest')} {index + 1}
                  </span>
                </div>
                <div className="form-wrapper row">
                  <FormGroupSelect
                    onChange={(gender) => onChangeField({ ...pax, gender }, index)}
                    value={pax.gender}
                    label={t('passenger.gender')}
                    className="col-md-4 col-12"
                    options={getGender(pax.paxTypeCode as PassengerType)}
                    disabled={pax.paxTypeCode !== PassengerType.Adult}
                    needToTranslate={true}
                  />
                  <FormGroup
                    name="firstName"
                    onChange={(firstName) => onChangeField({ ...pax, firstName }, index)}
                    value={pax.firstName}
                    label={t('passenger.fn')}
                    className="col-md-4 col-12"
                    validation={getPackagePassengerValidation(index).firstName}
                    isSubmitted={isSubmitted}
                  />
                  <FormGroup
                    name="lastName"
                    onChange={(lastName) => onChangeField({ ...pax, lastName }, index)}
                    value={pax.lastName}
                    label={t('passenger.ln')}
                    className="col-md-4 col-12"
                    validation={getPackagePassengerValidation(index).lastName}
                    isSubmitted={isSubmitted}
                  />
                </div>
                <div className="form-wrapper row">
                  {index === 0 && (
                    <Fragment>
                      <PhoneInput
                        onChange={(phone) => onChangeField({ ...pax, phone }, index)}
                        value={pax.phone}
                        isSubmitted={isSubmitted}
                        validation={getPackagePassengerValidation(index).phone}
                        className="col-md-8 col-12"
                      />
                      <FormGroup
                        name="email"
                        type="email"
                        onChange={(email) => onChangeField({ ...pax, email }, index)}
                        value={pax.email}
                        label={t('passenger.email')}
                        className="col-md-4 col-12"
                        validation={getPackagePassengerValidation(index).email}
                        isSubmitted={isSubmitted}
                      />
                    </Fragment>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

          <OrderTripCompany
            companyId={companyId}
            tripId={tripId}
            onChangeCompanyId={setCompanyId}
            onChangeTripId={setTripId}
          />

        <div className="checkbox-inline">
          <Checkbox
            checked={termsAccepted}
            onChange={setTermsAccepted}
            label={t('passenger.confirm')}
            className="mb-30"
            hasError={isSubmitted && !termsAccepted}
            blicked={true}
          />
          <span className="btn-lnk" onClick={() => setIsConditionsOpen(true)}>
            {t('passenger.read_more')}
          </span>
        </div>
        {isConditionsOpen && (
          <GeneralConditionsModal
            product="OfflinePackages"
            onClose={() => setIsConditionsOpen(false)}
          />
        )}
        
          <BookPayment
            onClickSend={onClickSend}
            onClickSubmit={onClickSubmit}
            disabled={isLoading}
            privileges={dealModel.privilegesInfo}
            credit2000={dealModel.credit2000PaymentInfo}
          />
        
      </div>
    </div>
  );
};

export default withErrorBoundary(OrderBook, 'OrderBook');
